// apolo/src/lib/loop/loopHealth.js — P4-T04 (code-tab-lift).
// @arch:pure-engine
//
// The Engineering-floor recovery GUARDS, consolidated into one PURE, tested module:
// the thresholds that decide when a run is unhealthy and must stop. They were inline
// literals scattered across runLocalAgentLoopInner (spiral `>= 4`, identical-content
// `= 4`); centralizing them makes the floor a SINGLE SOURCE OF TRUTH with unit tests
// pinning the exact numbers (L-3 — these thresholds are the felt-pain levers; the P1
// characterization + property net is the cross-check that the loop still trips them at
// the same point). The loop calls the predicates AT their existing sites — no control
// flow moved (L-1), only the threshold logic extracted.
//
// THRESHOLDS ARE FROZEN: changing one changes Engineering behavior (a regression).

/** Consecutive tool errors that abort a run as a tool_failure_spiral. */
export const MAX_CONSECUTIVE_TOOL_ERRORS = 4;
/** Identical tool-less replies in a row that abort as an identical_output_loop. */
export const MAX_IDENTICAL_CONTENT = 4;
/** Repeat-guard warnings (mesma tool+args) que abortam como tool_repeat_loop. */
export const MAX_REPEAT_WARNINGS = 3;

/** True when N consecutive tool errors have hit the spiral threshold. */
export function spiralTripped(consecutiveToolErrors) {
  return consecutiveToolErrors >= MAX_CONSECUTIVE_TOOL_ERRORS;
}

/** True when N identical tool-less replies have hit the anti-loop threshold. */
export function identicalTripped(identicalContentRun) {
  return identicalContentRun >= MAX_IDENTICAL_CONTENT;
}

/**
 * The consolidated floor verdict: the FIRST tripped guard's stopReason, else
 * 'continue'. Order = context_overflow → tool_failure_spiral → identical_output_loop
 * (overflow is detected on a thrown/failed send; the others on counters). The loop
 * checks these guards at their own sites; this function documents + tests the combined
 * floor and its ordering in one place.
 * @param {{ isOverflow?: boolean, consecutiveToolErrors?: number, identicalContentRun?: number }} state
 * @returns {'continue' | { stopReason: 'context_overflow'|'tool_failure_spiral'|'identical_output_loop' }}
 */
export function classifyLoopHealth({ isOverflow = false, consecutiveToolErrors = 0, identicalContentRun = 0 } = {}) {
  if (isOverflow) return { stopReason: 'context_overflow' };
  if (spiralTripped(consecutiveToolErrors)) return { stopReason: 'tool_failure_spiral' };
  if (identicalTripped(identicalContentRun)) return { stopReason: 'identical_output_loop' };
  return 'continue';
}

// FASE-12 E3-T03 — repeat-guard com caminho PRÓPRIO. O "Aviso: … já foi executada"
// casava resultIsError (^Aviso:) e contava como ERRO de tool → acelerava o
// spiral-abort e o run morria como 'tool_failure_spiral' (motivo ENGANOSO: o modelo
// não estava falhando tools, estava repetindo a mesma chamada). Aqui a rodada de
// pós-execução decide em UM lugar: repeat → contador próprio + 'tool_repeat_loop';
// erro real → spiral; sucesso → reset. Aplica os eventos e devolve stopReason|null.
import { applySpineEvent } from './loopRunState.js';
import { resultIsError } from './spinePredicates.js';
import { NUDGE_MARKER } from '../codeAgentMode.js';

// FASE-13 E12-T03 — erro de AMBIENTE (deps/node_modules/build) é RECUPERÁVEL: uma
// reinstalação resolve. Achado Kimi: node_modules corrompido → 4 erros de tool →
// 'tool_failure_spiral', quando `npm install` teria consertado. Estes shapes ganham
// UMA chance de reparo (nudge) antes de alimentar o spiral.
const ENV_ERROR_SHAPES = [
  /TAR_ENTRY_ERROR/i,
  /ERR_MODULE_NOT_FOUND/i,
  /cannot find module/i,
  /ENOENT[^\n]{0,120}node_modules/i,
  /node_modules[^\n]{0,120}ENOENT/i,
  /EACCES[^\n]{0,120}node_modules/i,
  /npm err!/i,
  // shell "binário sumiu": `sh: vite: command not found`, `zsh:1: vite: not found`
  /(?:sh|bash|zsh|dash):(?:\s*\d+:)?\s*[\w./-]+:\s*(?:command )?not found/i,
];

/** True quando o erro é de AMBIENTE quebrado (reinstalável), não da chamada. */
export function isRecoverableEnvError(result) {
  const text = String(result ?? '');
  if (!text) return false;
  return ENV_ERROR_SHAPES.some((re) => re.test(text));
}

// F13-E15-T04 (Reflexion) — doom-loop: muitas iterações AGINDO sem nenhuma
// edição numa tarefa de ENTREGA. Distinto do spiral (erros) e do repeat-guard
// (mesma call): aqui é atividade IMPRODUTIVA — ler/grepar sem convergir.
export const DOOM_LOOP_MIN_ITERATIONS = 10;

/** True quando a tarefa é de entrega e o loop agiu 10+ vezes sem editar. */
export function doomLoopTripped(s, { readOnly = false, planMode = false, isConversationalTask = false } = {}) {
  if (readOnly || planMode || isConversationalTask) return false;
  return s.iteration >= DOOM_LOOP_MIN_ITERATIONS && s.executedAnyTool && !s.wroteFile;
}

/** Nudge de reparo (1x por run): conserte o ambiente via run_command e SIGA. */
export const ENV_REPAIR_NUDGE = [
  `${NUDGE_MARKER} O erro acima é AMBIENTE QUEBRADO (deps/node_modules/build) — recuperável, não desista.`,
  'Conserte AGORA via run_command: `rm -rf node_modules && npm install` (ou `npx --yes <ferramenta>` como atalho) e repita a operação que falhou.',
  'PROIBIDO pedir pro usuário rodar comandos que VOCÊ pode rodar.',
].join(' ');

/** True when N repeat-guard warnings have hit the repeat-loop threshold. */
export function repeatTripped(repeatWarnings) {
  return repeatWarnings >= MAX_REPEAT_WARNINGS;
}

/**
 * Pós-execução de UMA tool call: aplica o evento de saúde correto e devolve o
 * stopReason terminal ('tool_repeat_loop' | 'tool_failure_spiral') ou null.
 * @param {import('./loopRunState.js').SpineState} s
 * @param {boolean} repeatWarned  o resultado foi o Aviso do repeat-guard
 * @param {string} result         corpo do tool result
 * @param {string} [name]         nome da tool (D-11: conta erro repetido por ferramenta)
 * @returns {'tool_repeat_loop'|'tool_failure_spiral'|'env_repair_nudge'|null}
 */
export function toolOutcomeStop(s, repeatWarned, result, name) {
  if (repeatWarned) {
    applySpineEvent(s, { type: 'repeat_warning' });
    // fix/repeat-loop-overfire (08-07, incidente Glyco) — o hard-stop só faz
    // sentido pra um loop IMPRODUTIVO. Um run que já ESCREVEU arquivos (entrega
    // real) e entra numa fase de re-leitura/verificação repetida NÃO é um loop
    // travado: o aviso soft (a tool fica bloqueada) já corta o desperdício, e
    // matar o turno com "Turno falhou" descartaria trabalho legítimo. writtenFiles
    // vazio = nada entregue ⇒ os patológicos (re-read puro, dependência fora do
    // ar) seguem tripando cedo (readRepeatLoop + repeatFailingDep continuam verdes).
    const unproductive = !s.writtenFiles || s.writtenFiles.length === 0;
    return repeatTripped(s.repeatWarnings) && unproductive ? 'tool_repeat_loop' : null;
  }
  if (resultIsError(result)) {
    // E12-T03 — ambiente quebrado ganha UMA chance de reparo (não é stop: o loop
    // injeta o ENV_REPAIR_NUDGE e segue). Reincidência volta a contar pro spiral.
    if (!s.envRepairNudged && isRecoverableEnvError(result)) {
      applySpineEvent(s, { type: 'env_repair_nudge' });
      return 'env_repair_nudge';
    }
    applySpineEvent(s, { type: 'tool_error', name });
    return spiralTripped(s.consecutiveToolErrors) ? 'tool_failure_spiral' : null;
  }
  applySpineEvent(s, { type: 'tool_success' });
  return null;
}

/**
 * D-11 pleno — a MESMA ferramenta falhou 2+ vezes no run (mesmo que ele termine
 * bem): vira lição ExpeL no Worker. Devolve o pior ofensor ou null.
 * @param {import('./loopRunState.js').SpineState|null} s
 */
export function maxRepeatedToolError(s) {
  let top = null;
  for (const [name, count] of s?.toolErrorsByName || []) {
    if (count >= 2 && (!top || count > top.count)) top = { name, count };
  }
  return top;
}

/**
 * T3+T4 (fix/repeat-loop-overfire) — descritor do repeat-loop pra notícia
 * terminal. Só para 'tool_repeat_loop'. `failing` = a ferramenta acumulou 2+
 * erros REAIS no run (D-11 toolErrorsByName) → dependência/MCP fora do ar, e a
 * notícia acusa a FERRAMENTA, não a tarefa do usuário. Puro; args já resumidos
 * pelo caller (summarizeToolArgs), pra não puxar deps do agentLoop pro loop/.
 * @param {string} stopReason
 * @param {string} name
 * @param {string} argsSummary
 * @param {{ toolErrorsByName?: Map<string,number> }} s
 * @returns {{ tool: string, args: string, failing: boolean }|undefined}
 */
export function buildRepeatInfo(stopReason, name, argsSummary, s) {
  if (stopReason !== 'tool_repeat_loop') return undefined;
  return { tool: name, args: argsSummary, failing: (s?.toolErrorsByName?.get(name) || 0) >= 2 };
}

export default classifyLoopHealth;
